import axios from 'axios';
import type { MerchantTransactionAmountLike } from './merchantAmounts';
import { apiUrl, cabinetPath, currentCabinetRole } from './domains';
import { getMerchantAccessToken, getStoredMerchantUser, isMerchantImpersonationActive, finishMerchantImpersonation, clearMerchantSession } from './merchantSession';

export interface ExternalPlatformStatisticsSummary {
    total_usdt: number;
    completed_count: number;
    reported_count: number;
    missing_usdt_count: number;
    gross_usdt: number;
    gross_count: number;
    net_usdt: number;
    net_count: number;
}

// Типы для API
export type RequisiteType = 'card' | 'sbp' | 'sbp_kgs' | 'sbp_qr' | 'mobile_topup' | 'mobile_kgs' | 'mobile_tjs' | 'qr_vietnam_sber' | 'qr_vietnam_vtb' | 'psb_ecom' | 'rub_transgran_uzs_emission_tbank' | 'rub_transgran_uzs_emission_vtb' | 'rub_transgran_uzs_emission_sber' | 'transgran_card_rub_arm' | 'transgran_mobile_rub_arm' | 'kzt_transgran_gel' | 'kzt_transgran_idr' | 'kzt_transgran_tjs' | 'kzt_transgran_uzs' | 'elqr';

export const REQUISITE_TYPE_OPTIONS: ReadonlyArray<{
    value: RequisiteType;
    label: string;
}> = [
        { value: 'card', label: 'card' },
        { value: 'sbp', label: 'sbp' },
        { value: 'sbp_kgs', label: 'sbp_kgs' },
        { value: 'sbp_qr', label: 'sbp_qr' },
        { value: 'mobile_kgs', label: 'mobile_kgs' },
        { value: 'mobile_tjs', label: 'Mobile Commerce TJS' },
        { value: 'qr_vietnam_sber', label: 'qr_vietnam_sber' },
        { value: 'qr_vietnam_vtb', label: 'qr_vietnam_vtb' },
        { value: 'psb_ecom', label: 'PSB E-com' },
        { value: 'rub_transgran_uzs_emission_tbank', label: 'rub_transgran_uzs_emission_tbank' },
        { value: 'rub_transgran_uzs_emission_vtb', label: 'rub_transgran_uzs_emission_vtb' },
        { value: 'rub_transgran_uzs_emission_sber', label: 'rub_transgran_uzs_emission_sber' },
        { value: 'transgran_card_rub_arm', label: 'transgran_card_rub_arm' },
        { value: 'transgran_mobile_rub_arm', label: 'transgran_mobile_rub_arm' },
        { value: 'kzt_transgran_gel', label: 'kzt_transgran_gel' },
        { value: 'kzt_transgran_idr', label: 'kzt_transgran_idr' },
        { value: 'kzt_transgran_tjs', label: 'kzt_transgran_tjs' },
        { value: 'kzt_transgran_uzs', label: 'kzt_transgran_uzs' },
        { value: 'elqr', label: 'elqr' },
    ];

export interface LoginRequest {
    username: string;
    password: string;
}

export interface RegisterRequest {
    username: string;
    email: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    token_type: string;
}

export interface MerchantTwoFactorChallengeResponse {
    two_factor_required: true;
    challenge_token: string;
}

export type MerchantLoginResponse =
    | LoginResponse
    | MerchantTwoFactorChallengeResponse;

export interface MerchantTwoFactorSetupResponse {
    otpauth_uri: string;
    manual_key: string;
}

export const TRADER_IMPERSONATION_TOKEN_KEY = 'trader_impersonation_access_token';

export const getTraderAccessToken = () => {
    return sessionStorage.getItem(TRADER_IMPERSONATION_TOKEN_KEY) || localStorage.getItem('trader_access_token');
};

export const isTraderImpersonationActive = () => {
    return Boolean(sessionStorage.getItem(TRADER_IMPERSONATION_TOKEN_KEY));
};

export const clearTraderImpersonation = () => {
    sessionStorage.removeItem(TRADER_IMPERSONATION_TOKEN_KEY);
};

export interface PasswordResetRequestResponse {
    status: string;
    message: string;
}

export interface PasswordResetInfo {
    user_type: 'trader' | 'merchant' | 'support';
    username: string;
    expires_at: string;
    is_valid: boolean;
}

export interface PasswordResetCompleteResponse {
    status: string;
    message: string;
    user_type: 'trader' | 'merchant' | 'support';
    redirect_url: string;
}

export interface UserResponse {
    id: string;
    username: string;
    email: string;
    created_at: string;
    is_active?: boolean;
    user_type?: 'trader' | 'merchant' | 'support';
    fiat_currency?: 'RUB' | 'KGS' | 'KZT';
    impersonation?: boolean;
    impersonated_by_admin_id?: string;
    impersonated_by_admin_username?: string;
}

export interface OrderCreate {
    from_currency: string;
    to_currency: string;
    from_amount: number;
    to_amount: number;
    order_type: string;
    min_amount: number;
    max_amount: number;
    duration_minutes: number;
    phone_number?: string;
    transfer_amount?: string;
    recipient_bank?: string;
    recipient_name?: string;
    recipient_card?: string;
    requisite_types?: Array<'sbp' | 'card' | 'elqr'>;
    qr_url?: string;
    notes?: string;
}

export interface OrderUpdate {
    requisite_types?: Array<'sbp' | 'card' | 'elqr'>;
    from_amount?: number;
    min_amount?: number;
    max_amount?: number;
    phone_number?: string;
    recipient_bank?: string;
    recipient_name?: string;
    recipient_card?: string;
    qr_url?: string;
    notes?: string;
    is_paused?: boolean;
}

export interface OrderResponse {
    automation?: { enabled: boolean; device_id?: string; bank?: string; version: string };
    capacity_policy_version?: number | null;
    id: string;
    user_id: string;
    from_currency: string;
    to_currency: string;
    from_amount: number;
    to_amount: number;
    order_type: string;
    status: string;
    created_at: string;
    expires_at?: string;
    duration_minutes: number;
    /** Current USDT estimate; null when the rate is unavailable. */
    remaining_amount?: number | null;
    remaining_fiat_amount?: number | null;
    min_amount?: number;
    max_amount?: number;
    phone_number?: string;
    transfer_amount?: string;
    recipient_bank?: string;
    recipient_name?: string;
    recipient_card?: string;
    requisite_types?: Array<'sbp' | 'card' | 'elqr'>;
    qr_url?: string;
    notes?: string;
    accumulated_amount?: number;
    reserved_amount?: number;
    /** @deprecated Historical order quote; new assignments use the current trader rate. */
    trader_exchange_rate?: number | null;
    /** @deprecated Historical order quote timestamp. */
    rate_fixed_at?: string | null;
    is_paused?: boolean;
}

export interface PaymentRequest {
    from_currency: string;
    to_currency: string;
    amount: number;
}

export interface PaymentResponse {
    order_id: string;
    transaction_id: string;
    from_currency: string;
    to_currency: string;
    requested_amount: number;
    order_from_amount: number;
    order_to_amount: number;
    phone_number?: string;
    transfer_amount?: string;
    recipient_bank?: string;
    recipient_name?: string;
    recipient_card?: string;
    qr_url?: string;
    notes?: string;
    order_type: string;
    user_id: string;
    created_at: string;
}

export interface WithdrawReceipt {
    id: string;
    filename?: string | null;
    size?: number | null;
    content_type?: string | null;
    uploaded_at?: string | null;
}

export interface PaymentTransactionResponse {
    assignment_version?: number;
    confirmation_method?: string;
    bank_receipt_id?: string;
    id: string;
    order_id?: string | null;
    user_id: string;
    merchantPaymentId?: string;
    merchantWithdrawalId?: string;
    payer_amount: number;
    payer_currency: string;
    receiver_amount: number;
    receiver_currency: string;
    exchange_rate?: number;
    // Статусы deposit: assigned, receipt_uploaded, confirmed, completed, cancelled, disputed
    // Статусы withdraw: pending_requisites, assigned, confirmed, completed, cancelled, disputed, no_traders
    // no_traders — трейдер не найден при создании вывода (финальный статус)
    // pending_requisites — трейдер найден, ожидается ввод реквизитов пользователем
    status: string;
    trader_commission_rate?: number;
    trader_commission_amount?: number;
    trader_commission_tier?: CommissionTier | null;
    trader_commission_mode?: 'fixed' | 'tiered' | string;
    trader_commission_scope?: 'method' | 'general' | 'fixed' | string;
    merchant_commission_rate?: number;
    merchant_commission_amount?: number;
    merchant_commission_tier?: CommissionTier | null;
    merchant_commission_mode?: 'fixed' | 'tiered' | string;
    merchant_commission_scope?: 'method' | 'general' | 'fixed' | string;
    merchant_credited_usdt?: number | null;
    merchant_reserved_usdt?: number | null;
    merchant_exchange_rate?: number | null;
    merchant_rate_source?: string | null;
    merchant_rate_markup_percent?: number | null;
    withdraw_receipts?: WithdrawReceipt[];
    withdraw_receipt_filename?: string;
    withdraw_receipt_uploaded_at?: string;
    receipt_filename?: string;
    receipt_uploaded_at?: string;
    assigned_at: string;
    duration_seconds: number;
    expires_at?: string;
    timing_policy?: string | null;
    server_time?: string;
    confirmed_at?: string;
    completed_at?: string;
    cancelled_at?: string;
    cancellation_reason?: string;
    order_info?: OrderResponse;
    notes?: string;
    user_requisites?: {
        phone_number?: string;
        recipient_bank?: string;
        recipient_name?: string;
        recipient_card?: string;
        mobile_operator?: string;
    };
    requisite_type?: RequisiteType | null;
    environment?: string;
}

export interface CommissionTier {
    min_amount: number;
    max_amount: number | null;
    commission_rate: number;
    max_amount_inclusive?: boolean;
}

export type CommissionTiersByRequisiteType = Partial<Record<RequisiteType, CommissionTier[]>>;

export interface ReceiptUploadResponse {
    success: boolean;
    filename: string;
    file_path: string;
    transaction_id: string;
    message: string;
}

export interface TraderSettings {
    timezone_offset_minutes?: number;
    user_id: string;
    fiat_currency: 'RUB' | 'KGS' | 'KZT';
    balance: number;
    insurance_balance?: number;
    trust_deposit?: number;
    frozen_balance?: number;
    reserved_balance?: number;
    deposit_commission_rate: number; // Комиссия за deposit (9%)
    withdraw_commission_rate: number; // Комиссия за withdraw (4%)
    deposit_commission_tiers?: CommissionTier[];
    withdraw_commission_tiers?: CommissionTier[];
    deposit_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
    withdraw_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
    exchange_rate?: number | null;
    updated_at: string;
}

export interface TraderSettingsUpdate {
    timezone_offset_minutes?: number;
    balance?: number;
    deposit_commission_rate?: number;
    withdraw_commission_rate?: number;
    deposit_commission_tiers?: CommissionTier[];
    withdraw_commission_tiers?: CommissionTier[];
    deposit_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
    withdraw_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
    rate_source?: string;
    rate_markup_percent?: number;
}

export interface TraderCommissionStats {
    total_commission_earned: number;
    today_commission_earned: number;
    last_commission_reset_date?: string;
}

// Типы для депозитов USDT
export interface TraderDepositCreate {
    expected_amount?: number;
}

export interface TraderDepositResponse {
    deposit_method?: 'wallet' | 'shared';
    requested_amount?: number;
    network?: string;
    expires_at?: string;
    server_time?: string;
    receive_tx_hash?: string;
    error_message?: string;
    id: string;
    deposit_address: string;
    status: string;
    expected_amount?: number;
    received_amount?: number;
    created_at: string;
    qr_code_data?: string;
    // Информация о подтверждении
    user_confirmed?: boolean;
    confirmed_at?: string;
    // Информация о комиссии
    platform_fee?: number;
    expected_credited_amount?: number;
    credited_amount?: number;
}

export interface TraderDepositConfirm {
    deposit_id: string;
    tx_hash?: string;
}

export interface TraderDepositList {
    deposits: TraderDepositResponse[];
    total: number;
    pending_count: number;
    total_received: number;
    page: number;
    size: number;
    pages: number;
}

export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    size: number;
    pages: number;
}

export interface OrderList {
    orders: OrderResponse[];
    total: number;
    page: number;
    size: number;
    pages: number;
}

export interface TraderTransactionList {
    transactions: PaymentTransactionResponse[];
    total: number;
    page: number;
    size: number;
    pages: number;
}

export interface MerchantPaymentListItem extends MerchantTransactionAmountLike {
    traffic_type?: 'ftd' | 'td' | 'std' | null;
    merchantOrderId?: string | null;
    payer_amount?: number;
    receiver_amount?: number;
    transaction_id: string;
    payment_id: string;
    user_id?: string;
    merchantPaymentId?: string;
    merchantWithdrawalId?: string;
    amount: number;
    currency: string;
    order_type: string;
    status: string;
    requisite_type?: string | null;
    created_at: string;
    confirmed_at?: string;
    completed_at?: string;
    merchant_settle?: boolean;
    merchant_settlement_id?: string | null;
}

export interface PaymentTransactionList {
    payments: MerchantPaymentListItem[];
    total: number;
    limit: number;
    offset: number;
    merchant_id: string;
    totals: any;
    page?: number;
    size?: number;
    pages?: number;
}

export interface AppealList {
    items: Appeal[];
    total: number;
    page: number;
    size: number;
    pages: number;
}

export interface DepositBalanceCheck {
    deposit_id: string;
    address: string;
    usdt_balance: number;
    trx_balance: number;
    status: string;
    has_funds: boolean;
}

export interface AppConfig {
    deployment: boolean;
    tron_network: string;
    is_production: boolean;
}

export interface AdminCurrencyRate {
    quoted_at?: string | null;
    rate_update_interval_seconds?: number;
    description?: string;
    is_constant?: boolean;
    source: string;
    source_label: string;
    is_default: boolean;
    from_currency: string;
    to_currency: string;
    rate: number | null;
    base_rate: number | null;
    markup_percent: number | null;
    configured_markup_percent: number;
    is_active: boolean;
    auto_update: boolean;
    updated_at: string | null;
    updated_by: string | null;
    has_data: boolean;
}

export interface SupportUserResponse {
    id: string;
    username: string;
    email: string;
    created_at: string;
    is_active: boolean;
    user_type: string;
}

export type AdminRole = 'viewer' | 'invite_manager' | 'full_access';

export interface AdminUserResponse extends SupportUserResponse {
    admin_role: AdminRole;
}

export enum AppealStatus {
    OPEN = 'open',
    PENDING_SUPPORT = 'pending_support',
    IN_PROGRESS = 'in_progress',
    RESOLVED = 'resolved',
    REJECTED = 'rejected',
    CANCELLED = 'cancelled',
}

export type SupportLocalStatus =
    | 'completed'
    | 'refund_to_client'
    | 'waiting_trader'
    | 'waiting_merchant_reply'
    | 'receipt_present_waiting_another'
    | 'no_receipt_waiting'
    | 'waiting_client_statement'
    | 'waiting_trader_statement';

export interface SupportLocalStatusHistoryEntry {
    previous_status: SupportLocalStatus | null;
    status: SupportLocalStatus | null;
    previous_comment: string | null;
    comment: string | null;
    changed_by: string;
    changed_by_id: string;
    changed_at: string;
}

export interface SupportLocalAnnotation {
    support_local_status?: SupportLocalStatus | null;
    support_local_status_comment?: string | null;
    support_local_status_history?: SupportLocalStatusHistoryEntry[];
}

export interface SupportAppeal extends Appeal, SupportLocalAnnotation {
    is_external?: boolean;
    locally_recalculable?: boolean;
    resolution_method?: string;
    recalculation_id?: string;
}

export interface SupportAppealList extends AppealList {
    items: SupportAppeal[];
}

export enum AppealResolution {
    FAVOR_MERCHANT = 'favor_merchant',
    FAVOR_TRADER = 'favor_trader',
}

export type TraderAppealReason = 'requisite_blocked' | 'invalid_receipt' | 'client_statement_required';

export interface Appeal {
    timer_reset_at?: string | null;
    id: string;
    transaction_id: string;
    merchant_id: string;
    trader_id?: string;
    quick_response_available?: boolean;
    status: AppealStatus;
    amount: number;
    currency: string;
    file_path?: string;
    description?: string;
    created_at: string;
    updated_at: string;
    resolution_type?: string;
    resolution_reason?: string;
    resolved_at?: string;
    resolved_by?: string;
    assigned_to?: string;
    assigned_at?: string;
    transaction_type?: string;
    merchant_name?: string;
    trader_name?: string;
    appeal_lifetime_minutes?: number;
    trader_requisites?: {
        phone_number?: string;
        recipient_bank?: string;
        recipient_name?: string;
        transfer_amount?: string;
        notes?: string;
    };
    user_requisites?: {
        phone_number?: string;
        recipient_bank?: string;
        recipient_name?: string;
        recipient_card?: string;
    };
    resolution_history?: Array<{
        resolution_type?: string;
        resolution_reason?: string;
        resolved_by?: string;
        resolved_at?: string;
        status?: string;
        reopened_by?: string;
        reopened_at?: string;
        reopen_description?: string;
        reopen_file_path?: string;
        trader_response_file_path?: string;
        trader_response_description?: string;
        trader_response_reason?: TraderAppealReason;
        trader_requested_amount?: number | null;
        trader_responded_at?: string;
    }>;
    attachments?: AppealAttachment[];
    externally_managed?: boolean;
    external_provider?: 'youpay' | 'flintpays' | string;
    external_provider_name?: string;
    external_appeal_id?: string;
    external_appeal_status?: string;
    external_reason_code?: string;
    external_comment?: string;
    external_ttl?: string | number;
    external_blocked?: boolean;
    external_client_invoice_required?: boolean;
    external_trader_invoice_required?: boolean;
    external_materials?: Array<{
        type?: string;
        path?: string;
        mime_type?: string;
    }>;
    external_synced_at?: string;
    external_sync_error?: string;
    // Ответ трейдера
    trader_response_reason?: TraderAppealReason;
    trader_requested_amount?: number | null;
    trader_response_file_path?: string;
    trader_response_description?: string;
    trader_responded_at?: string;
}

export interface AppealAttachment {
    id: string;
    type: 'initial_appeal' | 'trader_response' | 'reopen';
    file_path: string;
    original_filename?: string;
    description?: string;
    uploaded_by_role: 'merchant' | 'support' | 'trader' | 'system';
    uploaded_by?: string;
    created_at: string;
}

export interface SupportTransactionLookupItem {
    processing_source?: string;
    external_platform_id?: string;
    transaction_id: string;
    payment_id?: string;
    merchant_payment_id?: string;
    merchant_withdrawal_id?: string;
    order_id?: string;
    order_type: 'deposit' | 'withdraw';
    status: string;
    payer_amount: number;
    payer_currency: string;
    receiver_amount: number;
    receiver_currency: string;
    exchange_rate?: number;
    assigned_at: string;
    receipt_uploaded_at?: string;
    confirmed_at?: string;
    completed_at?: string;
    cancelled_at?: string;
    cancellation_reason?: string;
    receipt_filename?: string;
    notes?: string;
    trader_requisites?: Record<string, any>;
    user_requisites?: Record<string, any>;
    merchant?: { id: string; username: string; name: string };
    trader?: { id: string; username: string };
    appeal?: {
        id: string;
        status: string;
        created_at: string;
        resolution_type?: string;
    };
}

export interface ResolveAppealRequest {
    resolution: AppealResolution;
    reason: string;
}

export const API_BASE_URL = apiUrl();

// Создаем экземпляр axios
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Интерсептор для добавления токена к запросам
api.interceptors.request.use(
    (config) => {
        let token = null;
        const url = config.url || '';

        if (url.includes('/admin/')) {
            token = localStorage.getItem('admin_access_token');
        } else if (url.includes('/support/') || url.includes('/api/support/')) {
            token = localStorage.getItem('support_access_token');
        } else if (url.includes('/merchant/')) {
            token = getMerchantAccessToken();
        } else {
            token = getTraderAccessToken();
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Интерсептор для обработки ошибок авторизации
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            if (isMerchantImpersonationActive() && (error.config?.url || '').includes('/merchant/')) {
                finishMerchantImpersonation();
                return Promise.reject(error);
            }
            // Удаляем токен и перенаправляем на страницу входа
            if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/auth')) {
                const role = currentCabinetRole();
                if (role === 'admin') {
                    localStorage.removeItem('admin_access_token');
                    window.location.href = cabinetPath('admin', '/login');
                } else if (role === 'support') {
                    localStorage.removeItem('support_access_token');
                    window.location.href = cabinetPath('support', '/login');
                } else if (role === 'merchant') {
                    localStorage.removeItem('merchant_access_token');
                    window.location.href = cabinetPath('merchant', '/login');
                } else if (role === 'trader') {
                    sessionStorage.removeItem(TRADER_IMPERSONATION_TOKEN_KEY);
                    localStorage.removeItem('trader_access_token');
                    window.location.href = cabinetPath('trader', '/login');
                }
            }
        } else if (error.response?.status === 429) {
            // Обрабатываем ошибку блокировки за спам
            const errorDetail = error.response?.data?.detail || 'Превышен лимит запросов. Попробуйте позже.';
            console.warn('🚫 Блокировка:', errorDetail);
            // Можно добавить уведомление пользователю
        }
        return Promise.reject(error);
    }
);

export interface BankStatement {
    id: string;
    trader_id: string;
    trader_username: string;
    currency: string;
    requisite_label: string;
    period_from: string;
    period_to: string;
    comment: string;
    original_filename: string;
    file_size: number;
    content_type: string;
    created_at: string;
    author_id: string;
    author_username: string;
    author_role: 'trader' | 'admin';
}

export interface StatementOption { id: string; label: string }
export interface StatementPage<T> { items: T[]; total: number; page: number; size: number; pages: number }
export interface BankStatementFilters {
    trader_id?: string;
    currency?: string;
    period_from?: string;
    period_to?: string;
}

export const bankStatementsAPI = {
    list: async (audience: 'trader' | 'admin', params: BankStatementFilters & { page: number; size: number }): Promise<StatementPage<BankStatement>> =>
        (await api.get(`/${audience}/bank-statements`, { params })).data,
    traders: async (search: string, page: number): Promise<StatementPage<StatementOption>> =>
        (await api.get('/admin/bank-statements/traders', { params: { search, page, size: 20 } })).data,
    create: async (data: FormData, audience: 'trader' | 'admin' = 'trader'): Promise<BankStatement> =>
        (await api.post(`/${audience}/bank-statements`, data, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
    download: async (audience: 'trader' | 'admin', id: string): Promise<Blob> =>
        (await api.get(`/${audience}/bank-statements/${encodeURIComponent(id)}/download`, { responseType: 'blob' })).data,
};

// ============= TRADER AUTH API =============
export const traderAuthAPI = {
    // Регистрация трейдера
    register: async (data: RegisterRequest): Promise<UserResponse> => {
        const response = await api.post('/trader/auth/register', data);
        return response.data;
    },

    // Авторизация трейдера
    login: async (data: LoginRequest): Promise<LoginResponse> => {
        const response = await api.post('/trader/auth/login', data);
        return response.data;
    },

    // Получение информации о текущем трейдере
    getCurrentUser: async (): Promise<UserResponse> => {
        const response = await api.get('/trader/auth/me');
        return response.data;
    },

    requestPasswordReset: async (username: string): Promise<PasswordResetRequestResponse> => {
        const response = await api.post('/trader/auth/password-reset/request', { username });
        return response.data;
    },

    // Выход
    logout: () => {
        sessionStorage.removeItem(TRADER_IMPERSONATION_TOKEN_KEY);
        localStorage.removeItem('trader_access_token');
    },
};

// ============= MERCHANT AUTH API =============
export const merchantAuthAPI = {
    getAdvances: async (page = 1): Promise<MerchantAdvanceHistory> => {
        return (await api.get('/merchant/auth/advances', { params: { page } })).data;
    },
    getAdvance: async (): Promise<{ advance: MerchantAdvance | null }> => {
        return (await api.get('/merchant/auth/advance')).data;
    },
    // Регистрация мерчанта
    register: async (data: RegisterRequest & { company_name?: string }): Promise<UserResponse & { api_key: string; company_name?: string }> => {
        const response = await api.post('/merchant/auth/register', data);
        return response.data;
    },

    // Авторизация мерчанта
    login: async (data: LoginRequest): Promise<MerchantLoginResponse> => {
        const response = await api.post('/merchant/auth/login', data);
        return response.data;
    },

    completeTwoFactorLogin: async (
        challenge_token: string,
        code: string
    ): Promise<LoginResponse> => {
        const response = await api.post('/merchant/auth/login/2fa', {
            challenge_token,
            code,
        });
        return response.data;
    },

    // Получение информации о текущем мерчанте
    getCurrentUser: async (): Promise<UserResponse & {
        api_key: string;
        sandbox_api_key?: string;
        company_name?: string;
        webhook_url?: string;
        webhook_secret?: string;
        sandbox_webhook_url?: string;
        sandbox_webhook_secret?: string;
        rate_source?: string;
        rate_markup_percent?: number;
        exchange_rate?: number | null;
        deposit_commission_tiers?: CommissionTier[];
        withdraw_commission_tiers?: CommissionTier[];
        deposit_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        withdraw_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        requisite_types?: RequisiteType[];
        auto_unique_deposit_amount?: boolean;
        reject_deposit_on_amount_cooldown?: boolean;
        deposit_min_amount?: number | null;
        deposit_max_amount?: number | null;
        withdraw_min_amount?: number | null;
        withdraw_max_amount?: number | null;
        two_factor_enabled: boolean;
        api_ip_allowlist: string[];
        live_api_ip_allowlist?: string[];
        sandbox_api_ip_allowlist?: string[];
        integration_profile?: 'current' | 'typical_v2_2';
        fiat_currency?: 'RUB' | 'KGS' | 'KZT';
    }> => {
        const response = await api.get('/merchant/auth/me');
        return response.data;
    },

    // Обновить webhook URL
    updateWebhook: async (
        webhook_url: string,
        environment: 'live' | 'sandbox' = 'live'
    ): Promise<{ message: string; webhook_url: string; webhook_secret?: string; environment: 'live' | 'sandbox' }> => {
        const response = await api.put('/merchant/auth/webhook', { webhook_url, environment });
        return response.data;
    },

    requestPasswordReset: async (username: string): Promise<PasswordResetRequestResponse> => {
        const response = await api.post('/merchant/auth/password-reset/request', { username });
        return response.data;
    },

    setupTwoFactor: async (): Promise<MerchantTwoFactorSetupResponse> => {
        const response = await api.post('/merchant/auth/2fa/setup');
        return response.data;
    },

    enableTwoFactor: async (
        code: string
    ): Promise<{ status: string; two_factor_enabled: true }> => {
        const response = await api.post('/merchant/auth/2fa/enable', { code });
        return response.data;
    },

    disableTwoFactor: async (
        code: string
    ): Promise<{ status: string; two_factor_enabled: false }> => {
        const response = await api.post('/merchant/auth/2fa/disable', { code });
        return response.data;
    },

    changePassword: async (data: {
        current_password: string;
        new_password: string;
        new_password_confirmation: string;
        two_factor_code?: string;
    }): Promise<{ status: string; message: string }> => {
        const response = await api.put('/merchant/auth/password', data);
        return response.data;
    },

    updateIPAllowlist: async (
        addresses: string[],
        environment: 'live' | 'sandbox' | 'both' = 'both'
    ): Promise<{ status: string; addresses: string[]; environment: string }> => {
        const response = await api.put('/merchant/auth/ip-allowlist', { addresses, environment });
        return response.data;
    },

    // Выход
    logout: () => {
        clearMerchantSession();
    },
};

export const supportAuthAPI = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
        const response = await api.post('/support/auth/login', data);
        return response.data;
    },

    getCurrentUser: async (): Promise<SupportUserResponse> => {
        const response = await api.get('/support/auth/me');
        return response.data;
    },

    requestPasswordReset: async (username: string): Promise<PasswordResetRequestResponse> => {
        const response = await api.post('/support/auth/password-reset/request', { username });
        return response.data;
    },

    logout: () => {
        localStorage.removeItem('support_access_token');
    },
};

export const adminAuthAPI = {
    register: async (data: RegisterRequest): Promise<AdminUserResponse> => {
        const response = await api.post('/admin/auth/register', data);
        return response.data;
    },

    login: async (data: LoginRequest): Promise<LoginResponse> => {
        const response = await api.post('/admin/auth/login', data);
        return response.data;
    },

    getCurrentUser: async (): Promise<AdminUserResponse> => {
        const response = await api.get('/admin/auth/me');
        return response.data;
    },

    logout: () => {
        localStorage.removeItem('admin_access_token');
    },

};

export interface MerchantAdvance {
    total_returned?: number;
    outstanding?: number;
    balance: number;
    total_issued: number;
    started_at: string;
}

export interface AdvanceRequest {
    amount: string;
    comment: string;
    merchant_comment?: string;
    request_id: string;
}

export interface AdvanceRecord {
    kind?: 'issue' | 'return';
    outstanding_after?: number;
    _id: string;
    amount: number;
    created_at: string;
    admin_username: string;
    comment: string;
    merchant_comment?: string;
}

export interface MerchantAdvanceHistory {
    items: { id: string; created_at: string; amount: number; merchant_comment: string; kind?: 'issue' | 'return' }[];
    page: number;
    pages: number;
    total: number;
}

export interface MerchantPayoutLimit {
    limit: number;
    available: number;
    version: number;
}

export interface AdvanceHistory {
    payout_limit?: MerchantPayoutLimit;
    items: AdvanceRecord[];
    advance: MerchantAdvance | null;
    page: number;
    pages: number;
    total: number;
}

export interface ActualFinance {
    actual_settlement_total: number;
}

export interface SettlementTrader {
    id: string; username: string; balance: number; insurance_balance: number;
    reserved_balance: number; frozen_balance: number; available_balance: number; balance_ready: boolean;
}
export interface TraderSettlementRequest {
    request_id: string; amount: string; commission: string; comment: string;
}
export interface TraderSettlementRecord {
    _id: string; amount: number; commission: number; total: number; comment: string;
    balance_before: number; balance_after: number; created_at: string; admin_username: string;
}
export interface TraderSettlementHistory extends PaginatedResponse<TraderSettlementRecord> {
    trader: SettlementTrader;
}

export interface SettlementMerchant extends ActualFinance {
    advance?: MerchantAdvance | null;
    id: string;
    username: string;
    available_balance: number;
    settlement_balance: number;
    settlement_sequence: number;
}

export interface SettlementPeriod {
    date_from: string;
    date_to: string;
}

export interface SettlementExportRequest extends SettlementPeriod {
    fingerprint: string;
}

export interface SettlementExport {
    items: Array<Record<string, any> & { transaction_id: string; settlement_contribution_usdt: number | null }>;
    total: number;
    fingerprint: string;
}

export interface SettlementPreview extends SettlementPeriod, ActualFinance {
    first_transaction_id: string | null;
    last_transaction_id: string | null;
    transaction_count: number;
    deposit_count: number;
    withdraw_count: number;
    deposit_total_usdt: number;
    withdraw_total_usdt: number;
    amount: number;
    available_balance: number;
    balance_after: number;
    expected_sequence: number;
    fingerprint: string;
    can_settle: boolean;
    reason: string | null;
    invalid_transaction_ids: string[];
}

export type PlatformSettlementAmountSource = 'external_net_usdt' | 'external_reported_usdt';

export interface PlatformAmountComparison {
    our_total_usdt: number | null;
    provider_total_usdt: number | null;
    difference_usdt: number | null;
    our_missing_count: number;
    provider_missing_count: number;
}

export interface SettlementPlatform extends ActualFinance {
    id: string;
    name: string;
    available_balance: number | null;
    deposit_balance: number | null;
    withdraw_balance: number | null;
    settlement_balance: number;
    settlement_sequence: number;
    amount_source: PlatformSettlementAmountSource | null;
    balance_ready: boolean;
    balance_reason: string | null;
    missing_amount_count: number;
    balance_updated_at: string | null;
    can_change_source: boolean;
    comparison?: PlatformAmountComparison | null;
}

export interface PlatformSettlementHistory extends PaginatedResponse<SettlementRecord> {
    platform: SettlementPlatform;
}

export interface SettlementRecord extends SettlementPeriod {
    actual_amount?: number;
    _id: string;
    merchant_id?: string;
    external_platform_id?: string;
    amount_source?: PlatformSettlementAmountSource;
    sequence: number;
    amount: number;
    balance_before: number;
    balance_after: number;
    created_at: string;
    admin_username: string;
    comment: string;
    first_transaction_id: string;
    last_transaction_id: string;
    transaction_count: number;
    deposit_count: number;
    withdraw_count: number;
    deposit_total_usdt: number;
    withdraw_total_usdt: number;
}

export interface SettlementRequest extends SettlementPeriod {
    actual_amount: string;
    fingerprint: string;
    comment: string;
    request_id: string;
    expected_sequence: number;
}

export interface SettlementHistory extends PaginatedResponse<SettlementRecord> {
    merchant: SettlementMerchant;
}

export interface RecalculationInput {
    amount: string;
    complete_cancelled: boolean;
    cancel_completed?: boolean;
    cancel_assigned?: boolean;
    send_callback: boolean;
    reason: string;
    historical_parameters: Record<string, string>;
}
export interface RecalculationRequest extends RecalculationInput {
    fingerprint: string;
    request_id: string;
}
export interface RecalculationPreview {
    transaction_id: string;
    old_status: string;
    new_status: string;
    send_callback?: boolean;
    direction: string;
    currency: string;
    old_amount: number;
    new_amount: number;
    before: Record<string, any>;
    after: Record<string, number>;
    manual_parameters: Record<string, string>;
    entries: Array<{
        kind: string; account_id: string; name: string; delta: number;
        balance_before: number; balance_after: number;
        available_before: number; available_after: number; commission_delta: number;
        increments?: Record<string, number>;
    }>;
    can_apply: boolean;
    blocked_reason: string | null;
    fingerprint: string;
}
export interface RecalculationOperation {
    _id: string;
    phase: 'preparing' | 'sealed' | 'applying' | 'completed' | 'aborted';
    transaction_id: string;
    created_at: string;
    completed_at?: string;
    admin_username: string;
    actor_username?: string;
    actor_type?: string;
    appeal_id?: string;
    request: RecalculationRequest;
    preview: RecalculationPreview;
    error?: string;
    callback_status?: 'not_requested' | 'pending' | 'delivered' | 'superseded';
    callback_attempts?: number;
    callback_delivered_at?: string;
    callback_error?: string;
}
export interface RecalculationDetails {
    transaction: Record<string, any>;
    merchant: { id: string | null; name: string | null };
    executor: { kind: 'trader' | 'platform'; id: string | null; name: string | null };
    parameters: Record<string, number | null>;
    blocked_reason: string | null;
    history: RecalculationOperation[];
}

export interface WithdrawalTransferDetails {
    transaction_id: string; status: string; amount: number; currency: string;
    expires_at: string; trader_name: string | null; blocked_reason: string | null;
    candidates: { order_id: string; trader_id: string; username: string }[];
    unavailable?: { order_id: string; trader_id: string; username: string; reason: string }[];
    history: (WithdrawalTransferPreview & { operation_id: string; reason: string; admin_id: string; admin_username?: string | null; occurred_at: string })[];
}
export interface WithdrawalTransferPreview {
    fingerprint: string; target_order_id: string; target_trader_name: string;
    source_trader_id: string; target_trader_id: string;
    before: Record<string, number | null>; after: Record<string, number | null>;
}
export interface WithdrawalTransferRequest {
    target_order_id: string; operation_id: string; fingerprint: string;
    reason: string; unpaid_confirmed: boolean; loss_confirmed: boolean;
}
export interface AdminTraderDeposit {
    id: string; trader_id: string; trader_username?: string; deposit_method: 'wallet' | 'shared';
    status: string; network?: string; deposit_address: string; expected_amount?: number;
    received_amount?: number; credited_amount?: number; platform_fee?: number;
    created_at: string; expires_at?: string; received_at?: string; completed_at?: string;
    receive_tx_hash?: string; error_message?: string;
    manual_approval?: { admin_username: string; approved_at: string; warnings: string[]; comment?: string };
}
export interface DepositTransferPreview {
    tx_hash: string; sender: string; recipient: string; amount: string; timestamp: number;
    network: string; expected_amount: number; trader_id: string; warnings: string[];
    confirmed: boolean; preview_token: string;
}

export interface AdminDeviceEvent {
    id: string; event_id: string; trader_id: string; trader_name: string;
    device_id: string; device_name: string; bank: string; package_name: string;
    occurred_at: number; server_received_at: string | null; title: string; text: string;
    automation_status: 'triggered' | 'pending' | 'retrying' | 'not_triggered';
    transaction_id: string | null; transaction_status: string | null;
    automation_reason_code?: string | null; automation_reason?: string | null;
    requisite_type?: string | null; method_source?: 'transaction' | 'notification' | null;
}
export interface AdminDeviceEventFilters {
    automation_status?: AdminDeviceEvent['automation_status'];
    transaction_id?: string;
    trader_id?: string; device_id?: string; date_from?: string; date_to?: string;
}
export interface AdminDeviceEventOptions {
    traders: { id: string; name: string }[];
    devices: { id: string; name: string; trader_id: string }[];
}
export type AdminDeviceEventPage = PaginatedResponse<AdminDeviceEvent> & { retention_days: number };

export const adminAPI = {
    getCustomerAntifraudPermissions: async (merchantId: string): Promise<{ customer_ids: string[] }> =>
        (await api.get(`/admin/antifraud/merchants/${encodeURIComponent(merchantId)}`)).data,
    setCustomerAntifraudPermission: async (merchantId: string, customerId: string, allowed: boolean): Promise<{ customer_ids: string[] }> =>
        (await api.put(`/admin/antifraud/merchants/${encodeURIComponent(merchantId)}`, { customer_id: customerId, allowed })).data,
    getDeviceEvents: async (params: AdminDeviceEventFilters & { page: number; size: number }): Promise<AdminDeviceEventPage> =>
        (await api.get('/admin/device-events', { params })).data,
    getDeviceEventOptions: async (trader_id?: string): Promise<AdminDeviceEventOptions> =>
        (await api.get('/admin/device-events/filter-options', { params: { trader_id } })).data,
    getTraderDeposits: async (params: { page: number; trader_id?: string; status?: string; deposit_method?: string }): Promise<PaginatedResponse<AdminTraderDeposit>> =>
        (await api.get('/admin/trader-deposits', { params })).data,
    getTraderDeposit: async (id: string): Promise<AdminTraderDeposit> =>
        (await api.get(`/admin/trader-deposits/${encodeURIComponent(id)}`)).data,
    previewTraderDeposit: async (id: string, tx_hash: string): Promise<DepositTransferPreview> =>
        (await api.post(`/admin/trader-deposits/${encodeURIComponent(id)}/preview`, { tx_hash })).data,
    creditTraderDeposit: async (id: string, preview_token: string, comment: string): Promise<AdminTraderDeposit> =>
        (await api.post(`/admin/trader-deposits/${encodeURIComponent(id)}/credit`, { preview_token, ownership_confirmed: true, comment })).data,

    getWithdrawalTransfer: async (id: string): Promise<WithdrawalTransferDetails> =>
        (await api.get(`/admin/withdraw-reassignments/${encodeURIComponent(id)}`)).data,
    previewWithdrawalTransfer: async (id: string, target_order_id: string): Promise<WithdrawalTransferPreview> =>
        (await api.post(`/admin/withdraw-reassignments/${encodeURIComponent(id)}/preview`, { target_order_id })).data,
    transferWithdrawal: async (id: string, payload: WithdrawalTransferRequest): Promise<{ status: string }> =>
        (await api.post(`/admin/withdraw-reassignments/${encodeURIComponent(id)}`, payload)).data,
    getWithdrawalTransferResult: async (id: string, operation: string): Promise<{ status: string }> =>
        (await api.get(`/admin/withdraw-reassignments/${encodeURIComponent(id)}/operations/${encodeURIComponent(operation)}`)).data,
    getTransactionCallback: async (id: string): Promise<TransactionCallbackState> =>
        (await api.get(`/admin/recalculations/transactions/${encodeURIComponent(id)}/callback`)).data,
    resendTransactionCallback: async (id: string, request_id: string): Promise<TransactionCallbackState> =>
        (await api.post(`/admin/recalculations/transactions/${encodeURIComponent(id)}/callback`, { request_id })).data,
    getRecalculation: async (id: string): Promise<RecalculationDetails> =>
        (await api.get(`/admin/recalculations/transactions/${encodeURIComponent(id)}`)).data,
    previewRecalculation: async (id: string, data: RecalculationInput): Promise<RecalculationPreview> =>
        (await api.post(`/admin/recalculations/transactions/${encodeURIComponent(id)}/preview`, data)).data,
    createRecalculation: async (id: string, data: RecalculationRequest): Promise<RecalculationOperation> =>
        (await api.post(`/admin/recalculations/transactions/${encodeURIComponent(id)}`, data)).data,
    getRecalculationOperation: async (id: string): Promise<RecalculationOperation> =>
        (await api.get(`/admin/recalculations/operations/${encodeURIComponent(id)}`)).data,

    getSettlementPlatforms: async (page = 1): Promise<PaginatedResponse<SettlementPlatform>> => {
        return (await api.get('/admin/settlements/platforms', { params: { page } })).data;
    },
    configurePlatformSettlementSource: async (id: string, amount_source: PlatformSettlementAmountSource): Promise<SettlementPlatform> => {
        return (await api.put(`/admin/external-platforms/${id}/settlement-source`, { amount_source })).data;
    },
    getPlatformSettlements: async (id: string, page = 1): Promise<PlatformSettlementHistory> => {
        return (await api.get(`/admin/external-platforms/${id}/settlements`, { params: { page } })).data;
    },
    previewPlatformSettlement: async (id: string, data: SettlementPeriod): Promise<SettlementPreview> => {
        return (await api.post(`/admin/external-platforms/${id}/settlements/preview`, data)).data;
    },
    exportPlatformSettlement: async (id: string, data: SettlementExportRequest): Promise<SettlementExport> =>
        (await api.post(`/admin/external-platforms/${id}/settlements/export`, data)).data,
    createPlatformSettlement: async (id: string, data: SettlementRequest): Promise<{
        settlement: SettlementRecord; platform: SettlementPlatform;
    }> => {
        return (await api.post(`/admin/external-platforms/${id}/settlements`, data)).data;
    },

    getSettlementTraders: async (page = 1): Promise<PaginatedResponse<SettlementTrader>> =>
        (await api.get('/admin/settlements/traders', { params: { page } })).data,
    getTraderSettlements: async (id: string, page = 1): Promise<TraderSettlementHistory> =>
        (await api.get(`/admin/traders/${id}/settlements`, { params: { page } })).data,
    createTraderSettlement: async (id: string, data: TraderSettlementRequest): Promise<{
        settlement: TraderSettlementRecord; trader: SettlementTrader;
    }> => (await api.post(`/admin/traders/${id}/settlements`, data)).data,

    getSettlementMerchants: async (page = 1): Promise<PaginatedResponse<SettlementMerchant>> => {
        return (await api.get('/admin/settlements/merchants', { params: { page } })).data;
    },
    setMerchantPayoutLimit: async (merchantId: string, data: { limit: string; expected_version: number }): Promise<MerchantPayoutLimit> => {
        return (await api.patch(`/admin/merchants/${merchantId}/payout-limit`, data)).data;
    },
    getAdvances: async (merchantId: string, page = 1): Promise<AdvanceHistory> => {
        return (await api.get(`/admin/merchants/${merchantId}/advances`, { params: { page } })).data;
    },
    returnAdvance: async (merchantId: string, data: AdvanceRequest): Promise<{ record: AdvanceRecord; advance: MerchantAdvance }> => {
        return (await api.post(`/admin/merchants/${merchantId}/advances/returns`, data)).data;
    },
    issueAdvance: async (merchantId: string, data: AdvanceRequest): Promise<{ record: AdvanceRecord; advance: MerchantAdvance }> => {
        return (await api.post(`/admin/merchants/${merchantId}/advances`, data)).data;
    },
    getSettlements: async (merchantId: string, page = 1): Promise<SettlementHistory> => {
        return (await api.get(`/admin/merchants/${merchantId}/settlements`, { params: { page } })).data;
    },
    previewSettlement: async (merchantId: string, data: SettlementPeriod): Promise<SettlementPreview> => {
        return (await api.post(`/admin/merchants/${merchantId}/settlements/preview`, data)).data;
    },
    exportSettlement: async (id: string, data: SettlementExportRequest): Promise<SettlementExport> =>
        (await api.post(`/admin/merchants/${id}/settlements/export`, data)).data,
    createSettlement: async (merchantId: string, data: SettlementRequest): Promise<{
        settlement: SettlementRecord; merchant: SettlementMerchant;
    }> => {
        return (await api.post(`/admin/merchants/${merchantId}/settlements`, data)).data;
    },
    getTraders: async (page: number = 1, size: number = 50, includeStats: boolean = true, referralCode?: string): Promise<PaginatedResponse<any>> => {
        let url = `/admin/traders?page=${page}&size=${size}&include_stats=${includeStats}`;
        if (referralCode) {
            url += `&referral_code=${encodeURIComponent(referralCode)}`;
        }
        const response = await api.get(url);
        return response.data;
    },

    getReferralCodes: async (): Promise<string[]> => {
        const response = await api.get('/admin/referral-codes');
        return response.data;
    },

    updateTrader: async (traderId: string, data: any): Promise<any> => {
        const response = await api.patch(`/admin/traders/${traderId}`, data);
        return response.data;
    },

    resetTraderBalance: async (traderId: string): Promise<{
        status: string;
        trader_id: string;
        old_balance: number;
        new_balance: number;
    }> => {
        const response = await api.post(`/admin/traders/${traderId}/reset-balance`);
        return response.data;
    },

    createMerchantImpersonationToken: async (merchantId: string): Promise<LoginResponse> => {
        const response = await api.post(`/admin/merchants/${merchantId}/impersonation-token`);
        return response.data;
    },

    createTraderImpersonationToken: async (traderId: string): Promise<LoginResponse> => {
        const response = await api.post(`/admin/traders/${traderId}/impersonation-token`);
        return response.data;
    },

    getMerchants: async (page: number = 1, size: number = 50, includeStats: boolean = true, search?: string, fiatCurrency?: 'RUB' | 'KGS' | 'KZT'): Promise<PaginatedResponse<any>> => {
        const response = await api.get('/admin/merchants', {
            params: { page, size, include_stats: includeStats, search, fiat_currency: fiatCurrency },
        });
        return response.data;
    },

    updateMerchant: async (merchantId: string, data: any): Promise<any> => {
        const response = await api.patch(`/admin/merchants/${merchantId}`, data);
        return response.data;
    },

    getExternalPlatforms: async (page: number = 1, size: number = 50): Promise<PaginatedResponse<any>> => {
        const response = await api.get(`/admin/external-platforms?page=${page}&size=${size}`);
        return response.data;
    },

    getExternalPlatformCapabilities: async (fiatCurrency: 'RUB' | 'KGS' | 'KZT'): Promise<{
        fiat_currency: 'RUB' | 'KGS' | 'KZT';
        deposit: RequisiteType[];
        withdrawal: RequisiteType[];
        all: RequisiteType[];
    }> => {
        const response = await api.get(`/admin/external-platforms/capabilities/${fiatCurrency}`);
        return response.data;
    },

    createExternalPlatform: async (data: any): Promise<any> => {
        const response = await api.post('/admin/external-platforms', data);
        return response.data;
    },

    updateExternalPlatform: async (platformId: string, data: any): Promise<any> => {
        const response = await api.patch(`/admin/external-platforms/${platformId}`, data);
        return response.data;
    },

    getExternalPlatformStatistics: async (platformId: string, dateFrom: string, dateTo: string): Promise<{
        deposit: ExternalPlatformStatisticsSummary;
        withdraw: ExternalPlatformStatisticsSummary;
    }> => {
        const response = await api.get(`/admin/external-platforms/${platformId}/statistics`, {
            params: { date_from: dateFrom, date_to: dateTo },
        });
        return response.data;
    },

    getExternalPlatformDebugEvents: async (platformId: string, page: number = 1, size: number = 50): Promise<PaginatedResponse<any>> => {
        const response = await api.get(`/admin/external-platforms/${platformId}/debug-events?page=${page}&size=${size}`);
        return response.data;
    },

    getSupportUsers: async (page: number = 1, size: number = 50): Promise<PaginatedResponse<any>> => {
        const response = await api.get(`/admin/support?page=${page}&size=${size}`);
        return response.data;
    },

    updateSupport: async (supportId: string, data: any): Promise<any> => {
        const response = await api.patch(`/admin/support/${supportId}`, data);
        return response.data;
    },

    getPlatformSettings: async (): Promise<any> => {
        const response = await api.get('/admin/settings/platform');
        return response.data;
    },

    updatePlatformSettings: async (data: {
        deposit_fee_usd: number;
        merchant_api_requests_per_5_seconds: number;
        merchant_api_request_mixpanel_enabled: boolean;
        merchant_api_min_amount: number;
        merchant_api_max_amount: number;
    }): Promise<any> => {
        const response = await api.patch('/admin/settings/platform', data);
        return response.data;
    },

    getGlobalStats: async (): Promise<any> => {
        const response = await api.get('/admin/stats/global');
        return response.data;
    },

    createConstantRate: async (data: { name: string; description?: string; rate: number; from_currency: string; to_currency: string }): Promise<AdminCurrencyRate> => {
        const response = await api.post('/admin/constant-rates', data);
        return response.data;
    },

    updateConstantRate: async (source: string, data: { name: string; description?: string; rate: number }): Promise<AdminCurrencyRate> => {
        const response = await api.patch(`/admin/constant-rates/${encodeURIComponent(source)}`, data);
        return response.data;
    },

    updateRateSourceMetadata: async (rate: AdminCurrencyRate, data: { name: string; description: string }): Promise<void> => {
        await api.patch(`/admin/currency-rates/${encodeURIComponent(rate.from_currency)}/${encodeURIComponent(rate.to_currency)}/${encodeURIComponent(rate.source)}/metadata`, data);
    },

    getCurrencyRates: async (): Promise<AdminCurrencyRate[]> => {
        const response = await api.get('/admin/currency-rates');
        return response.data;
    },

    getSupportKPI: async (): Promise<any[]> => {
        const response = await api.get('/admin/stats/support');
        return response.data;
    },

    getAuditLogs: async (page: number = 1, size: number = 15): Promise<PaginatedResponse<any>> => {
        const response = await api.get(`/admin/logs?page=${page}&size=${size}`);
        return response.data;
    },

    createInvite: async (data: {
        user_type: string;
        email: string;
        username: string;
        integration_profile?: 'current' | 'typical_v2_2';
        deposit_commission_rate?: number;
        withdraw_commission_rate?: number;
        deposit_commission_tiers?: CommissionTier[];
        withdraw_commission_tiers?: CommissionTier[];
        deposit_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        withdraw_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        insurance_balance?: number;
        trust_deposit?: number;
        appeal_lifetime_minutes?: number;
        merchant_deposit_commission_rate?: number;
        merchant_withdraw_commission_rate?: number;
        merchant_deposit_commission_tiers?: CommissionTier[];
        merchant_withdraw_commission_tiers?: CommissionTier[];
        merchant_deposit_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        merchant_withdraw_commission_tiers_by_requisite_type?: CommissionTiersByRequisiteType;
        merchant_deposit_min_amount?: number | null;
        merchant_deposit_max_amount?: number | null;
        merchant_withdraw_min_amount?: number | null;
        merchant_withdraw_max_amount?: number | null;
        requisite_types?: RequisiteType[];
    }): Promise<any> => {
        const response = await api.post('/admin/invites', data);
        return response.data;
    },

    getTransactions: async (
        page: number = 1,
        size: number = 50,
        status?: string,
        orderType?: string,
        search?: string,
        filters?: {
            merchantId?: string;
            providerType?: 'trader' | 'external';
            providerId?: string;
            startDate?: string;
            endDate?: string;
            paymentMethod?: string;
            requisiteType?: RequisiteType;
            fiatCurrency?: 'RUB' | 'KGS' | 'KZT';
            amountMin?: string;
            amountMax?: string;
            requisitesSearch?: string;
            sortBy?: string;
            sortOrder?: string;
        }
    ): Promise<PaginatedResponse<any>> => {
        let url = `/admin/transactions?page=${page}&size=${size}`;
        if (status && status !== 'all') url += `&status=${encodeURIComponent(status)}`;
        if (orderType && orderType !== 'all') url += `&order_type=${encodeURIComponent(orderType)}`;
        if (search?.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
        if (filters?.merchantId) url += `&merchant_id=${encodeURIComponent(filters.merchantId)}`;
        if (filters?.providerType) url += `&provider_type=${encodeURIComponent(filters.providerType)}`;
        if (filters?.providerId) url += `&provider_id=${encodeURIComponent(filters.providerId)}`;
        if (filters?.startDate) url += `&start_date=${encodeURIComponent(filters.startDate)}`;
        if (filters?.endDate) url += `&end_date=${encodeURIComponent(filters.endDate)}`;
        if (filters?.paymentMethod && filters.paymentMethod !== 'all') url += `&payment_method=${encodeURIComponent(filters.paymentMethod)}`;
        if (filters?.requisiteType) url += `&requisite_type=${encodeURIComponent(filters.requisiteType)}`;
        if (filters?.fiatCurrency) url += `&fiat_currency=${encodeURIComponent(filters.fiatCurrency)}`;
        if (filters?.amountMin?.trim()) url += `&amount_min=${encodeURIComponent(filters.amountMin.trim())}`;
        if (filters?.amountMax?.trim()) url += `&amount_max=${encodeURIComponent(filters.amountMax.trim())}`;
        if (filters?.requisitesSearch?.trim()) url += `&requisites_search=${encodeURIComponent(filters.requisitesSearch.trim())}`;
        if (filters?.sortBy) url += `&sort_by=${encodeURIComponent(filters.sortBy)}`;
        if (filters?.sortOrder) url += `&sort_order=${encodeURIComponent(filters.sortOrder)}`;
        const response = await api.get(url);
        return response.data;
    },

    getTransactionFilterOptions: async (): Promise<{
        merchants: Array<{ id: string; username: string; company_name?: string | null }>;
        providers: Array<{ id: string; name: string; type: 'trader' | 'external' }>;
    }> => {
        const response = await api.get('/admin/transactions/filter-options');
        return response.data;
    },

    getInvites: async (page: number = 1, size: number = 50, status?: string): Promise<PaginatedResponse<any>> => {
        let url = `/admin/invites?page=${page}&size=${size}`;
        if (status) {
            url += `&status=${status}`;
        }
        const response = await api.get(url);
        return response.data;
    },

    getPasswordResetRequestsCount: async (): Promise<{ count: number }> => {
        const response = await api.get('/admin/invites/password-reset-requests/count');
        return response.data;
    },

    createPasswordResetLink: async (inviteId: string): Promise<any> => {
        const response = await api.post(`/admin/invites/${inviteId}/password-reset-link`);
        return response.data;
    },

    resetMerchantTwoFactor: async (
        inviteId: string
    ): Promise<{ status: string; two_factor_enabled: false }> => {
        const response = await api.post(`/admin/invites/${inviteId}/two-factor-reset`);
        return response.data;
    },

    getPaymentMethods: async (): Promise<string[]> => {
        const response = await api.get('/admin/stats/payment-methods');
        return response.data;
    },

    getConversionStats: async (params: {
        page?: number;
        size?: number;
        paymentType?: string;
        paymentMethod?: string;
        requisiteType?: RequisiteType;
        startDate?: string;
        endDate?: string;
    }): Promise<PaginatedResponse<any>> => {
        const { page = 1, size = 10, paymentType, paymentMethod, requisiteType, startDate, endDate } = params;
        let url = `/admin/stats/conversion?page=${page}&size=${size}`;
        if (paymentType && paymentType !== 'all') url += `&payment_type=${encodeURIComponent(paymentType)}`;
        if (paymentMethod && paymentMethod !== 'all') url += `&payment_method=${encodeURIComponent(paymentMethod)}`;
        if (requisiteType) url += `&requisite_type=${encodeURIComponent(requisiteType)}`;
        if (startDate) url += `&start_date=${encodeURIComponent(startDate)}`;
        if (endDate) url += `&end_date=${encodeURIComponent(endDate)}`;
        const response = await api.get(url);
        return response.data;
    },

    getDailyConversionStats: async (params: {
        page?: number;
        size?: number;
        paymentType?: string;
        paymentMethod?: string;
        requisiteType?: RequisiteType;
        startDate?: string;
        endDate?: string;
    }): Promise<PaginatedResponse<any>> => {
        const { page = 1, size = 10, paymentType, paymentMethod, requisiteType, startDate, endDate } = params;
        let url = `/admin/stats/conversion/daily?page=${page}&size=${size}`;
        if (paymentType && paymentType !== 'all') url += `&payment_type=${encodeURIComponent(paymentType)}`;
        if (paymentMethod && paymentMethod !== 'all') url += `&payment_method=${encodeURIComponent(paymentMethod)}`;
        if (requisiteType) url += `&requisite_type=${encodeURIComponent(requisiteType)}`;
        if (startDate) url += `&start_date=${encodeURIComponent(startDate)}`;
        if (endDate) url += `&end_date=${encodeURIComponent(endDate)}`;
        const response = await api.get(url);
        return response.data;
    },
};

export const passwordResetAPI = {
    getInfo: async (token: string): Promise<PasswordResetInfo> => {
        const response = await api.get(`/password-reset/${token}`);
        return response.data;
    },

    complete: async (token: string, password: string): Promise<PasswordResetCompleteResponse> => {
        const response = await api.post(`/password-reset/${token}/complete`, { password });
        return response.data;
    },
};

export const inviteAPI = {
    getInvite: async (token: string): Promise<any> => {
        const response = await api.get(`/invite/${token}`);
        return response.data;
    },

    completeRegistration: async (token: string, password: string): Promise<any> => {
        const response = await api.post(`/invite/${token}/complete`, { password });
        return response.data;
    },
};


// ============= MERCHANT API =============
// Создаем отдельный API instance для мерчантов с API ключом
const createMerchantAPI = (apiKeyOverride?: string) => {
    const merchantApiInstance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Добавляем интерцептор для автоматического добавления API ключа
    merchantApiInstance.interceptors.request.use((config) => {
        const user = getStoredMerchantUser();
        const apiKey = apiKeyOverride || user?.api_key;
        if (apiKey) {
            config.headers.Authorization = `Bearer ${apiKey}`;
        }
        return config;
    });

    return merchantApiInstance;
};

export interface SandboxTransaction {
    _id: string;
    payment_id: string;
    merchant_payment_id?: string;
    merchant_withdrawal_id?: string;
    user_id: string;
    order_type: 'deposit' | 'withdraw';
    status: string;
    fiat_amount: number;
    fiat_currency?: string;
    payer_amount?: number;
    payer_currency?: string;
    receiver_amount?: number;
    receiver_currency?: string;
    requisite_type?: RequisiteType | null;
    exchange_rate?: number;
    merchant_commission_rate?: number;
    merchant_commission_amount?: number;
    merchant_credited_usdt?: number | null;
    merchant_reserved_usdt?: number | null;
    merchant_exchange_rate?: number | null;
    merchant_rate_source?: string | null;
    merchant_rate_markup_percent?: number | null;
    callback_url?: string;
    assigned_at: string;
    confirmed_at?: string;
    completed_at?: string;
    updated_at?: string;
}

export interface SandboxWebhookLog {
    _id: string;
    transaction_id: string;
    payment_id?: string;
    webhook_url?: string;
    status?: string;
    delivered: boolean;
    http_status?: number;
    error?: string;
    payload?: any;
    signature?: string;
    created_at: string;
}

export type TypicalMethod = import('./typicalV2').TypicalMethod;

export interface TypicalSandboxDepositRequest {
    order_id: string;
    amount: number;
    currency: 'RUB' | 'KGS' | 'KZT';
    method: TypicalMethod;
    customer_account_id?: string;
    callback_url: string;
    success_url?: string | null;
    pending_url?: string | null;
    fail_url?: string | null;
}

export interface TypicalSandboxWithdrawalRequest {
    order_id: string;
    amount: number;
    currency: 'RUB' | 'KGS' | 'KZT';
    method: Exclude<TypicalMethod, 'elqr'>;
    customer_account_id?: string;
    callback_url: string;
    account_number: string;
    fields: {
        bank_id: string;
        first_name: string;
        last_name: string;
        [key: string]: unknown;
    };
}

export interface TypicalSandboxCreateResponse {
    amount?: number;
    /** Зафиксированный курс в валюте платежа за 1 USDT; только для депозитов. */
    rate?: number;
    transaction_id: string;
    redirect_url?: string;
    amount_usdt: number;
    balance_delta: number;
}

const signTypicalRequestBody = async (body: string, signingSecret: string): Promise<string> => {
    if (!window.crypto?.subtle) {
        throw new Error('Браузер не поддерживает Web Crypto для создания x-signature');
    }

    const encoder = new TextEncoder();
    const key = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(signingSecret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const signature = await window.crypto.subtle.sign('HMAC', key, encoder.encode(body));
    return Array.from(new Uint8Array(signature))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
};

const createTypicalSandboxTransaction = async (
    path: '/api/merchant/v2/deposits' | '/api/merchant/v2/withdrawals',
    sandboxApiKey: string,
    signingSecret: string,
    data: TypicalSandboxDepositRequest | TypicalSandboxWithdrawalRequest
): Promise<TypicalSandboxCreateResponse> => {
    const body = JSON.stringify(data);
    const signature = await signTypicalRequestBody(body, signingSecret);
    const response = await axios.post(`${API_BASE_URL}${path}`, body, {
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': sandboxApiKey,
            'x-signature': signature,
        },
        transformRequest: [(requestBody) => requestBody],
    });
    return response.data;
};

export const merchantSandboxAPI = {
    createPayment: async (
        sandboxApiKey: string,
        data: { user_id: string; amount: number; merchantPaymentId: string; callback?: string }
    ): Promise<any> => {
        const merchantApi = createMerchantAPI(sandboxApiKey);
        const response = await merchantApi.post('/api/merchant/payments', data);
        return response.data;
    },

    createWithdrawal: async (
        sandboxApiKey: string,
        data: { user_id: string; amount: number; merchantWithdrawalId: string; callback?: string }
    ): Promise<any> => {
        const merchantApi = createMerchantAPI(sandboxApiKey);
        const response = await merchantApi.post('/api/merchant/withdrawals', data);
        return response.data;
    },

    createTypicalDeposit: async (
        sandboxApiKey: string,
        signingSecret: string,
        data: TypicalSandboxDepositRequest
    ): Promise<TypicalSandboxCreateResponse> => {
        return createTypicalSandboxTransaction(
            '/api/merchant/v2/deposits', sandboxApiKey, signingSecret, data
        );
    },

    createTypicalWithdrawal: async (
        sandboxApiKey: string,
        signingSecret: string,
        data: TypicalSandboxWithdrawalRequest
    ): Promise<TypicalSandboxCreateResponse> => {
        return createTypicalSandboxTransaction(
            '/api/merchant/v2/withdrawals', sandboxApiKey, signingSecret, data
        );
    },

    getTransactions: async (
        sandboxApiKey: string,
        page: number = 1,
        limit: number = 20,
        filters?: {
            start_date?: string;
            end_date?: string;
            status?: string;
            order_type?: string;
        }
    ): Promise<{ transactions: SandboxTransaction[]; total: number; page: number; size: number; pages: number }> => {
        const merchantApi = createMerchantAPI(sandboxApiKey);
        const response = await merchantApi.get('/api/merchant/sandbox/transactions', {
            params: { page, limit, ...filters },
        });
        return response.data;
    },

    simulateStatus: async (
        sandboxApiKey: string,
        transactionId: string,
        data: { status: string; sendWebhook: boolean; reason?: string }
    ): Promise<any> => {
        const merchantApi = createMerchantAPI(sandboxApiKey);
        const response = await merchantApi.post(
            `/api/merchant/sandbox/transactions/${transactionId}/simulate`,
            data
        );
        return response.data;
    },

    getWebhookLogs: async (
        sandboxApiKey: string,
        limit: number = 20
    ): Promise<{ logs: SandboxWebhookLog[] }> => {
        const merchantApi = createMerchantAPI(sandboxApiKey);
        const response = await merchantApi.get('/api/merchant/sandbox/webhook-logs', {
            params: { limit },
        });
        return response.data;
    },
};

export const merchantAPI = {
    // Создать платеж (пополнение)
    createPayment: async (data: { user_id: string; amount: number; merchantPaymentId: string; callback?: string }): Promise<any> => {
        const merchantApi = createMerchantAPI();
        const response = await merchantApi.post('/api/merchant/payments', data);
        return response.data;
    },

    // Создать заявку на вывод
    createWithdrawal: async (data: { user_id: string; amount: number; merchantWithdrawalId: string; callback?: string }): Promise<any> => {
        const merchantApi = createMerchantAPI();
        const response = await merchantApi.post('/api/merchant/withdrawals', data);
        return response.data;
    },

    // Получить статистику
    getStats: async (): Promise<any> => {
        if (isMerchantImpersonationActive()) {
            const response = await api.get('/merchant/cabinet/stats');
            return response.data;
        }
        const merchantApi = createMerchantAPI();
        const response = await merchantApi.get('/api/merchant/stats');
        return response.data;
    },

    // Получить список платежей
    getPayments: async (
        page: number = 1,
        limit: number = 50,
        filters?: { start_date?: string; end_date?: string; status?: string; order_type?: string; transaction_id?: string; search?: string }
    ): Promise<PaymentTransactionList> => {
        const merchantApi = isMerchantImpersonationActive() ? api : createMerchantAPI();
        const paymentsPath = isMerchantImpersonationActive() ? '/merchant/cabinet/payments' : '/api/merchant/payments';

        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (filters?.search?.trim()) params.append('search', filters.search.trim());
        if (filters?.start_date) params.append('start_date', filters.start_date);
        if (filters?.end_date) params.append('end_date', filters.end_date);
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters?.order_type && filters.order_type !== 'all') params.append('order_type', filters.order_type);

        const response = await merchantApi.get(`${paymentsPath}?${params.toString()}`);
        return response.data;
    },

    // Получить статус платежа
    getPaymentStatus: async (transactionId: string): Promise<any> => {
        if (isMerchantImpersonationActive()) {
            const response = await api.get(`/merchant/cabinet/payments/${transactionId}/status`);
            return response.data;
        }
        const merchantApi = createMerchantAPI();
        const response = await merchantApi.get(`/api/merchant/payments/${transactionId}/status`);
        return response.data;
    },
};


export const ordersAPI = {
    setAutomation: async (id: string, enabled: boolean, device_id?: string): Promise<void> => {
        await api.patch(`/orders/${id}/automation`, { enabled, device_id });
    },
    // Создать ордер
    createOrder: async (data: OrderCreate): Promise<OrderResponse> => {
        const response = await api.post('/orders/', data);
        return response.data;
    },

    // Получить мои ордера
    getMyOrders: async (page: number = 1, limit: number = 50, filters?: {
        order_type?: 'deposit' | 'withdraw'; archived?: boolean;
        status?: 'active' | 'completed' | 'cancelled'; start_date?: string; end_date?: string;
    }): Promise<OrderList> => {
        const response = await api.get(`/orders/my?page=${page}&limit=${limit}`, { params: filters });
        return response.data;
    },

    // Получить активные ордера
    getActiveOrders: async (page: number = 1, limit: number = 50): Promise<OrderList> => {
        const response = await api.get(`/orders/active?page=${page}&limit=${limit}`);
        return response.data;
    },

    // Отменить ордер
    cancelOrder: async (orderId: string): Promise<{ message: string }> => {
        const response = await api.patch(`/orders/${orderId}/cancel`);
        return response.data;
    },

    resetAccumulated: async (orderId: string): Promise<OrderResponse> => {
        const response = await api.post(`/orders/${orderId}/reset-accumulated`);
        return response.data;
    },

    // Обновить ордер
    updateOrder: async (orderId: string, data: OrderUpdate): Promise<OrderResponse> => {
        const response = await api.patch(`/orders/${orderId}`, data);
        return response.data;
    },

    // Получить ордер по ID
    getOrder: async (orderId: string): Promise<OrderResponse> => {
        const response = await api.get(`/orders/${orderId}`);
        return response.data;
    },

    // Получить доступные валюты для deposit (без авторизации)
    getAvailableCurrencies: async (): Promise<string[]> => {
        const response = await api.get('/public/currencies');
        return response.data;
    },


};

export const paymentsAPI = {
    // Назначить ордер для платежа
    assignPaymentOrder: async (data: PaymentRequest): Promise<PaymentResponse> => {
        const response = await api.post('/payments/assign', data);
        return response.data;
    },

    // Получить доступные валютные пары для deposit
    getAvailablePairs: async (): Promise<{ available_pairs: string[], total_orders: number }> => {
        const response = await api.get('/payments/available-pairs');
        return response.data;
    },

    // Получить доступные диапазоны сумм для платежей
    getAvailableRanges: async (): Promise<{
        available_ranges: Record<string, Array<{
            min_amount: number;
            max_amount: number;
            remaining_capacity: number;
            trader_id: string;
        }>>;
        total_active_orders: number;
    }> => {
        const response = await api.get('/payments/available-ranges');
        return response.data;
    },

    // Загрузить чек
    uploadReceipt: async (transactionId: string, file: File): Promise<ReceiptUploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/payments/upload-receipt/${transactionId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    confirmTransfer: async (transactionId: string): Promise<{ message: string; transaction_id: string; status: string }> => {
        const response = await api.post(`/payments/user-confirm-transfer/${transactionId}`);
        return response.data;
    },

    // Получить чек
    getReceipt: (transactionId: string): string => {
        return `${api.defaults.baseURL}/payments/receipt/${transactionId}`;
    },

    uploadWithdrawReceipt: async (transactionId: string, file: File, assignmentVersion = 0, receiptId?: string): Promise<{
        withdraw_receipts: WithdrawReceipt[]; withdraw_receipt_filename: string; withdraw_receipt_uploaded_at: string;
    }> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('assignment_version', String(assignmentVersion));
        if (receiptId) formData.append('receipt_id', receiptId);
        const { data } = await api.post(`/payments/withdraw-receipt/${transactionId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },

    downloadWithdrawReceipt: async (transactionId: string, filename: string, receiptId?: string): Promise<void> => {
        const { data } = await api.get(`/payments/withdraw-receipt/${transactionId}`, {
            responseType: 'blob', params: receiptId ? { receipt_id: receiptId } : undefined,
        });
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url; link.download = filename;
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    },

    // Подтвердить платеж (трейдер)
    confirmPayment: async (transactionId: string, notes?: string, assignmentVersion = 0): Promise<{ message: string, transaction_id: string, already_confirmed?: boolean }> => {
        try {
            const response = await api.post(`/payments/confirm/${transactionId}`, {
                transaction_id: transactionId,
                notes: notes || null,
                assignment_version: assignmentVersion
            });
            return response.data;
        } catch (error) {
            const status = (error as { response?: { status?: number } }).response?.status;
            if (status === 401 || status === 403 || status === 429) throw error;
            // Another confirmation may have won while this request was in flight.
            try {
                const { data } = await api.get<PaymentTransactionResponse>(`/payments/status/${transactionId}`);
                if (data.id === transactionId && (data.assignment_version || 0) === assignmentVersion
                    && (data.status === 'confirmed' || data.status === 'completed')) {
                    return { message: 'Платёж уже подтверждён', transaction_id: transactionId, already_confirmed: true };
                }
            } catch {
                // Preserve the confirmation error if the current state is unknown.
            }
            throw error;
        }
    },

    // Отменить платеж (трейдер)
    cancelPayment: async (transactionId: string, assignmentVersion = 0): Promise<{ message: string }> => {
        const response = await api.post(`/payments/cancel/${transactionId}`, null, { params: { assignment_version: assignmentVersion } });
        return response.data;
    },

    // Отменить платеж (пользователь, публичный эндпоинт)
    userCancelPayment: async (transactionId: string): Promise<{ message: string; status: string }> => {
        const response = await api.post(`/payments/user-cancel/${transactionId}`);
        return response.data;
    },

    completeSandboxPayment: async (paymentId: string): Promise<{ transaction_id: string; environment: 'sandbox'; status: string }> => {
        const response = await api.post(`/payments/sandbox/payment/${paymentId}/complete`);
        return response.data;
    },

    cancelSandboxPayment: async (paymentId: string): Promise<{ transaction_id: string; environment: 'sandbox'; status: string }> => {
        const response = await api.post(`/payments/sandbox/payment/${paymentId}/cancel`);
        return response.data;
    },

    // Отправить реквизиты пользователя для вывода
    submitUserRequisites: async (
        transactionId: string,
        requisites: {
            phone_number?: string;
            recipient_bank?: string;
            recipient_name?: string;
            recipient_card?: string;
            mobile_operator?: string;
        }
    ): Promise<{ message: string; status: string }> => {
        const params = new URLSearchParams();
        if (requisites.phone_number) params.append('phone_number', requisites.phone_number);
        if (requisites.recipient_bank) params.append('recipient_bank', requisites.recipient_bank);
        if (requisites.recipient_name) params.append('recipient_name', requisites.recipient_name);
        if (requisites.recipient_card) params.append('recipient_card', requisites.recipient_card);
        if (requisites.mobile_operator) params.append('mobile_operator', requisites.mobile_operator);

        const response = await api.post(`/payments/submit-user-requisites/${transactionId}?${params.toString()}`);
        return response.data;
    },

    // Подтвердить получение средств пользователем (для вывода)
    confirmReceipt: async (transactionId: string): Promise<{ message: string; status: string }> => {
        const response = await api.post(`/payments/confirm-receipt/${transactionId}`);
        return response.data;
    },

    // Получить активные платежи для трейдера
    getTraderActivePayments: async (page: number = 1, limit: number = 50, orderType?: 'deposit' | 'withdraw'): Promise<TraderTransactionList> => {
        const response = await api.get(`/payments/trader/active?page=${page}&limit=${limit}${orderType ? `&order_type=${orderType}` : ''}`);
        return response.data;
    },

    // Получить историю операций для трейдера
    getTraderHistory: async (
        page: number = 1,
        limit: number = 50,
        filters?: { start_date?: string; end_date?: string; status?: string; order_type?: string; transaction_id?: string; requisites_search?: string }
    ): Promise<TraderTransactionList> => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (filters?.start_date) params.append('start_date', filters.start_date);
        if (filters?.end_date) params.append('end_date', filters.end_date);
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters?.order_type && filters.order_type !== 'all') params.append('order_type', filters.order_type);

        if (filters?.transaction_id) params.append('transaction_id', filters.transaction_id);
        if (filters?.requisites_search?.trim()) params.append('requisites_search', filters.requisites_search.trim());
        const response = await api.get(`/payments/trader/history?${params.toString()}`);
        return response.data;
    },

    getTraderAppeals: async (
        page: number = 1,
        limit: number = 50,
        filters?: { status?: string; start_date?: string; end_date?: string; search?: string }
    ): Promise<AppealList> => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters?.start_date) params.append('start_date', filters.start_date);
        if (filters?.end_date) params.append('end_date', filters.end_date);

        if (filters?.search?.trim()) params.append('search', filters.search.trim());

        const response = await api.get(`/payments/trader/appeals?${params.toString()}`);
        return response.data;
    },

    getTraderAppealsCount: async (): Promise<{ count: number }> => {
        const response = await api.get('/payments/trader/appeals/count');
        return response.data;
    },

    respondToAppeal: async (appealId: string, files: File[], description: string, reason?: TraderAppealReason, requestedAmount?: string): Promise<{ status: string; message: string; appeal_id: string }> => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        formData.append('description', description);
        if (reason) formData.append('reason', reason);
        if (requestedAmount !== undefined) formData.append('requested_amount', requestedAmount);
        const response = await api.post(`/payments/trader/appeals/${appealId}/respond`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    acceptAppeal: async (appealId: string): Promise<{ status: string; message: string; appeal_id: string }> => {
        const response = await api.post(`/payments/trader/appeals/${appealId}/accept`);
        return response.data;
    },

    // Получить статус платежа (без авторизации)
    getPaymentStatus: async (transactionId: string): Promise<PaymentTransactionResponse> => {
        const response = await api.get(`/payments/status/${transactionId}`);
        return response.data;
    },
};

// Настройки трейдера
export const traderAPI = {
    // Получить настройки трейдера
    getSettings: async (): Promise<TraderSettings> => {
        const response = await api.get('/orders/trader/settings');
        return response.data;
    },

    // Обновить настройки трейдера
    updateSettings: async (settings: TraderSettingsUpdate): Promise<{
        timezone_offset_minutes: number;
        message: string;
        balance: number;
        deposit_commission_rate: number;
        withdraw_commission_rate: number;
    }> => {
        const response = await api.put('/orders/trader/settings', settings);
        return response.data;
    },

    // Получить статистику баланса трейдера
    getLimitStats: async (): Promise<{
        balance: number;
        active_commitments: number | null;
        available_balance: number | null;
        usage_percentage: number | null;
        frozen_balance: number;
    }> => {
        const response = await api.get('/orders/trader/limit-stats');
        return response.data;
    },

    // Получить статистику заработка с комиссий
    getCommissionStats: async (): Promise<TraderCommissionStats> => {
        const response = await api.get('/orders/trader/commission-stats');
        return response.data;
    },

    getWithdrawLimitStatus: async (): Promise<WithdrawLimitStatus> => {
        const response = await api.get('/orders/trader/withdraw-limit');
        return response.data;
    },
};

export interface WithdrawLimitStatus {
    completed_count_2h: number;
    limit: number;
    is_limited: boolean;
    next_available_at: string | null;
    remaining: number;
}

// Интерфейс для лимитов депозита
export interface DepositLimits {
    deposit_method?: 'wallet' | 'shared';
    platform_fee?: number;
    max_deposit_amount: number;
    insurance_balance: number;
    trust_deposit: number;
    current_balance: number;
}

// API для депозитов USDT
export const depositsAPI = {
    getLimits: async (): Promise<DepositLimits> => {
        const response = await api.get('/deposits/limits');
        return response.data;
    },

    // Создать депозит
    createDeposit: async (data: TraderDepositCreate): Promise<TraderDepositResponse> => {
        const response = await api.post('/deposits/create', data);
        return response.data;
    },

    // Получить список депозитов
    getDepositsList: async (status?: string, page: number = 1, limit: number = 50): Promise<TraderDepositList> => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page.toString());
        params.append('limit', limit.toString());

        const response = await api.get(`/deposits/list?${params.toString()}`);
        return response.data;
    },

    // Получить депозит по ID
    getDeposit: async (depositId: string): Promise<TraderDepositResponse> => {
        const response = await api.get(`/deposits/${depositId}`);
        return response.data;
    },

    // Подтвердить депозит
    confirmDeposit: async (data: TraderDepositConfirm): Promise<{
        message: string;
        deposit_id: string;
        status: string;
    }> => {
        const response = await api.post('/deposits/confirm', data);
        return response.data;
    },

    // Проверить баланс депозитного адреса
    checkDepositBalance: async (depositId: string): Promise<DepositBalanceCheck> => {
        const response = await api.post(`/deposits/${depositId}/check`);
        return response.data;
    },

    // Отменить депозит
    cancelDeposit: async (depositId: string): Promise<{ message: string, deposit_id: string, status: string }> => {
        const response = await api.post(`/deposits/${depositId}/cancel`);
        return response.data;
    },
};

export const supportAPI = {
    getTransactionCallback: async (id: string): Promise<TransactionCallbackState> =>
        (await api.get(`/api/support/recalculations/transactions/${encodeURIComponent(id)}/callback`)).data,
    resendTransactionCallback: async (id: string, request_id: string): Promise<TransactionCallbackState> =>
        (await api.post(`/api/support/recalculations/transactions/${encodeURIComponent(id)}/callback`, { request_id })).data,
    getRecalculation: async (id: string): Promise<RecalculationDetails> =>
        (await api.get(`/api/support/recalculations/transactions/${encodeURIComponent(id)}`)).data,
    previewRecalculation: async (id: string, data: RecalculationInput): Promise<RecalculationPreview> =>
        (await api.post(`/api/support/recalculations/transactions/${encodeURIComponent(id)}/preview`, data)).data,
    createRecalculation: async (id: string, data: RecalculationRequest): Promise<RecalculationOperation> =>
        (await api.post(`/api/support/recalculations/transactions/${encodeURIComponent(id)}`, data)).data,
    getRecalculationOperation: async (id: string): Promise<RecalculationOperation> =>
        (await api.get(`/api/support/recalculations/operations/${encodeURIComponent(id)}`)).data,
    getAppealRecalculation: async (id: string): Promise<RecalculationDetails> =>
        (await api.get(`/api/support/appeals/${encodeURIComponent(id)}/recalculation`)).data,
    previewAppealRecalculation: async (id: string, data: RecalculationInput): Promise<RecalculationPreview> =>
        (await api.post(`/api/support/appeals/${encodeURIComponent(id)}/recalculation/preview`, data)).data,
    createAppealRecalculation: async (id: string, data: RecalculationRequest): Promise<RecalculationOperation> =>
        (await api.post(`/api/support/appeals/${encodeURIComponent(id)}/recalculation`, data)).data,

    getAppeals: async (status?: string, start_date?: string, end_date?: string, page: number = 1, limit: number = 50, search?: string): Promise<SupportAppealList> => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (start_date) params.append('start_date', start_date);
        if (end_date) params.append('end_date', end_date);
        if (search?.trim()) params.append('search', search.trim());
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        const response = await api.get(`/api/support/appeals?${params.toString()}`);
        return response.data;
    },

    updateLocalStatus: async (appealId: string, support_local_status: SupportLocalStatus | null, support_local_status_comment?: string | null): Promise<SupportLocalAnnotation> => {
        const response = await api.patch(`/api/support/appeals/${appealId}/local-status`, { support_local_status, support_local_status_comment });
        return response.data;
    },

    resolveAppeal: async (appealId: string, data: ResolveAppealRequest): Promise<{ status: string; message: string }> => {
        const response = await api.post(`/api/support/appeals/${appealId}/resolve`, data);
        return response.data;
    },

    assignAppeal: async (appealId: string): Promise<{ status: string; message: string }> => {
        const response = await api.post(`/api/support/appeals/${appealId}/assign`);
        return response.data;
    },

    lookupTransactions: async (query: string): Promise<{
        items: SupportTransactionLookupItem[];
        total: number;
        query: string;
    }> => {
        const response = await api.get(`/api/support/transactions/lookup?query=${encodeURIComponent(query.trim())}`);
        return response.data;
    },

    getAppealProof: (filePath: string): string => {
        const filename = filePath.split('/').pop();
        return `${api.defaults.baseURL}/uploads/appeals/${filename}`;
    },

    getAppealFileUrl: (filePath: string): string => {
        const normalizedPath = filePath.replace(/^\/+/, '');
        return `${api.defaults.baseURL}/${normalizedPath}`;
    },

    reopenAppeal: async (appealId: string, files: File[], description?: string): Promise<{ status: string; message: string; appeal_id: string }> => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        if (description) {
            formData.append('description', description);
        }
        const response = await api.post(`/api/support/appeals/${appealId}/reopen`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
};

// Получить конфигурацию приложения
export const getAppConfig = async (): Promise<AppConfig> => {
    const response = await api.get('/config');
    return response.data;
};

export default api;

export interface TraderDevice {
    id: string;
    name: string;
    status: 'pending' | 'active' | 'revoked';
    events_enabled?: boolean;
    created_at: string;
    paired_at: string | null;
    last_seen_at: string | null;
    pairing_expires_at: string | null;
    state_fresh: boolean;
    online: boolean;
    state: {
        notification_access: boolean;
        listener_connected: boolean;
        sms_permission: boolean;
        keep_screen_on: boolean;
        monitor_running: boolean;
        battery_unrestricted: boolean;
        notification_permission: boolean;
        app_version: string;
        android_version: number;
    } | null;
}

export interface DevicePairingKey {
    device: TraderDevice;
    key: string;
    expires_at: string;
}

export interface TraderNotification {
    id: string;
    event_id: string;
    occurred_at: number;
    title: string;
    text: string;
    device_id: string;
    device_name: string;
    package_name: string | null;
    transaction_id: string | null;
    requisite: { order_id: string; bank: string | null; type: string | null; card: string | null } | null;
}

export interface TraderNotificationsPage {
    items: TraderNotification[];
    total: number;
    page: number;
    page_size: number;
}

export const traderDevicesAPI = {
    events: async (params: Omit<AdminDeviceEventFilters, 'trader_id'> & { page: number; size: number }): Promise<AdminDeviceEventPage> =>
        (await api.get('/trader/devices/events', { params })).data,
    eventOptions: async (): Promise<Pick<AdminDeviceEventOptions, 'devices'>> =>
        (await api.get('/trader/devices/events/filter-options')).data,
    notifications: async (page: number, pageSize: number, transactionId?: string): Promise<TraderNotificationsPage> =>
        (await api.get('/trader/devices/notifications', { params: { page, page_size: pageSize, transaction_id: transactionId?.trim() || undefined } })).data,
    setEnabled: async (id: string, enabled: boolean): Promise<TraderDevice> => (await api.patch(`/trader/devices/${id}/capture`, { enabled })).data,
    list: async (): Promise<TraderDevice[]> => (await api.get('/trader/devices')).data.devices,
    create: async (name: string): Promise<DevicePairingKey> => (await api.post('/trader/devices', { name })).data,
    pairingKey: async (id: string): Promise<DevicePairingKey> => (await api.post(`/trader/devices/${id}/pairing-key`)).data,
    rename: async (id: string, name: string): Promise<TraderDevice> => (await api.patch(`/trader/devices/${id}`, { name })).data,
    revoke: async (id: string): Promise<void> => { await api.delete(`/trader/devices/${id}`); },
};

export interface TransactionCallbackState {
    event: {
        id?: string;
        source?: 'manual' | 'typical' | 'recalculation' | 'local';
        status: 'unknown' | 'pending' | 'retry' | 'sending' | 'delivered' | 'superseded';
        next_attempt_at?: string | null;
        delivered_at?: string | null;
        error?: string | null;
        attempts?: number;
    };
    can_resend: boolean;
    blocked_reason: string | null;
}
